import { z } from 'zod';

/**
 * Configurações da loja armazenadas como JSON tipado na tabela companies.
 * Sempre use `parseXSettings(raw)` para ler (aplica defaults e ignora lixo).
 */

// ---------------------------------------------------------------- Geral
export const generalSettingsSchema = z.object({
  currency: z.string().default('BRL'),
  locale: z.string().default('pt-BR'),
  timezone: z.string().default('America/Sao_Paulo'),
  dateFormat: z.enum(['dd/MM/yyyy', 'yyyy-MM-dd', 'MM/dd/yyyy']).default('dd/MM/yyyy'),
  hideValuesByDefault: z.boolean().default(false),
  soundOnNewOrder: z.boolean().default(true),
});
export type GeneralSettings = z.infer<typeof generalSettingsSchema>;

// ---------------------------------------------------------------- Recibo
export const receiptSettingsSchema = z.object({
  paperWidth: z.enum(['58mm', '80mm', 'a4']).default('80mm'),
  showLogo: z.boolean().default(true),
  headerText: z.string().max(300).default(''),
  footerText: z.string().max(300).default('Obrigado pela preferência! Volte sempre.'),
  showDocument: z.boolean().default(true),
  showAddress: z.boolean().default(true),
  showPhone: z.boolean().default(true),
  showCustomer: z.boolean().default(true),
  showSeller: z.boolean().default(true),
  autoPrint: z.boolean().default(false),
  copies: z.number().int().min(1).max(3).default(1),
});
export type ReceiptSettings = z.infer<typeof receiptSettingsSchema>;

// ---------------------------------------------------------------- Vendas
export const salesSettingsSchema = z.object({
  // Alçada padrão de desconto para vendedores/gerentes (dono e administradores não têm limite).
  maxDiscountPercent: z.number().int().min(0).max(100).default(20),
  allowNegativeStock: z.boolean().default(true),
  requireCustomer: z.boolean().default(false),
  commissionEnabled: z.boolean().default(false),
  defaultCommissionPercent: z.number().min(0).max(100).default(0),
  surchargeEnabled: z.boolean().default(false),
  surchargeLabel: z.string().max(40).default('Taxa de serviço'),
  surchargePercent: z.number().min(0).max(100).default(0),
  catalogStockMode: z.enum(['on_confirm', 'on_create']).default('on_confirm'),
  requireOpenCashSession: z.boolean().default(false),
  defaultPosView: z.enum(['grid', 'list']).default('grid'),
});
export type SalesSettings = z.infer<typeof salesSettingsSchema>;

// ---------------------------------------------------------------- Entrega
export const deliveryZoneSchema = z.object({
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(80),
  feeCents: z.number().int().min(0),
});

/** Serviços do frete calculado no padrão dos Correios. */
export const SHIPPING_SERVICES = ['pac', 'sedex'] as const;
export type ShippingServiceKey = (typeof SHIPPING_SERVICES)[number];

/** Entrega própria (motoboy) cobrada por distância entre a loja e o CEP do cliente. */
export const distanceFeeSchema = z.object({
  /** Taxa mínima, que já cobre os primeiros `includedKm`. */
  baseFeeCents: z.number().int().min(0).default(600),
  includedKm: z.number().min(0).max(100).default(2),
  /** Valor por km adicional (distância de rua estimada). */
  perKmCents: z.number().int().min(0).default(150),
  /** Raio máximo atendido (km de rua). Acima disso a entrega local não aparece. */
  maxKm: z.number().min(0.5).max(300).default(15),
  /** Arredonda o total para cima no múltiplo (ex.: 50 = R$ 0,50). 0 = sem arredondar. */
  roundToCents: z.number().int().min(0).max(1000).default(50),
  /** Distância de rua ≈ linha reta × fator (1,3 é a média urbana brasileira). */
  roadFactor: z.number().min(1).max(2).default(1.3),
});
export type DistanceFeeSettings = z.infer<typeof distanceFeeSchema>;
const DEFAULT_DISTANCE_FEE: DistanceFeeSettings = { baseFeeCents: 600, includedKm: 2, perKmCents: 150, maxKm: 15, roundToCents: 50, roadFactor: 1.3 };

/** Frete calculado (padrão de cálculo dos Correios: peso real × cúbico, faixa de destino e serviço). */
export const calculatedShippingSchema = z.object({
  services: z.array(z.enum(SHIPPING_SERVICES)).min(1).max(2).default(['pac', 'sedex']),
  /** Ajuste percentual sobre a tabela (ex.: -15 para quem tem contrato com desconto). */
  adjustPercent: z.number().min(-90).max(200).default(0),
  /** Embalagem e manuseio, somados uma vez por pedido. */
  handlingFeeCents: z.number().int().min(0).default(0),
  /** Dias para postar, somados ao prazo do serviço. */
  extraDays: z.number().int().min(0).max(30).default(1),
  /** Contratar valor declarado (seguro) pelo valor dos produtos. */
  declaredValue: z.boolean().default(false),
  /** Embalagem padrão para produtos sem peso ou medidas cadastrados. */
  defaultPackage: z
    .object({
      weightGrams: z.number().int().min(1).max(30000).default(300),
      lengthCm: z.number().min(1).max(100).default(25),
      widthCm: z.number().min(1).max(100).default(20),
      heightCm: z.number().min(1).max(100).default(5),
    })
    .default({ weightGrams: 300, lengthCm: 25, widthCm: 20, heightCm: 5 }),
});
export type CalculatedShippingSettings = z.infer<typeof calculatedShippingSchema>;
const DEFAULT_CALCULATED_SHIPPING: CalculatedShippingSettings = {
  services: ['pac', 'sedex'],
  adjustPercent: 0,
  handlingFeeCents: 0,
  extraDays: 1,
  declaredValue: false,
  defaultPackage: { weightGrams: 300, lengthCm: 25, widthCm: 20, heightCm: 5 },
};

/**
 * Regra de frete por região (desconto geral, por estado, cidade, faixa de CEP ou raio da loja).
 * Avaliadas em ordem; a primeira regra ativa que casar com o destino, a modalidade e o valor
 * mínimo é aplicada. Ex.: frete grátis em Goiânia acima de R$ 199; 50% para GO; R$ 10 fixo até 5 km.
 */
export const shippingRuleSchema = z.object({
  id: z.string().min(1).max(40),
  name: z.string().trim().min(1).max(80),
  active: z.boolean().default(true),
  scope: z.enum(['all', 'states', 'cities', 'cep_ranges', 'radius']).default('all'),
  states: z.array(z.string().regex(/^[A-Z]{2}$/)).max(27).default([]),
  cities: z
    .array(z.object({ city: z.string().trim().min(1).max(80), state: z.string().regex(/^[A-Z]{2}$/) }))
    .max(200)
    .default([]),
  cepRanges: z
    .array(z.object({ from: z.string().regex(/^\d{8}$/), to: z.string().regex(/^\d{8}$/) }))
    .max(100)
    .default([]),
  radiusKm: z.number().min(0.5).max(3000).nullable().default(null),
  appliesTo: z.enum(['all', 'local', 'shipping']).default('all'),
  /** free = frete grátis; percent = % de desconto (value 1..100); fixed = desconto em centavos; fixed_price = frete sai por `value` centavos. */
  effect: z.enum(['free', 'percent', 'fixed', 'fixed_price']).default('free'),
  value: z.number().int().min(0).default(0),
  minOrderCents: z.number().int().min(0).nullable().default(null),
  /** Datas no formato AAAA-MM-DD (inclusive), no fuso da loja. */
  startsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
  endsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
  /** Texto curto exibido no catálogo (ex.: "Frete grátis em Goiânia acima de R$ 199"). Vazio = gerado. */
  badge: z.string().trim().max(80).default(''),
});
export type ShippingRule = z.infer<typeof shippingRuleSchema>;

export const deliverySettingsSchema = z.object({
  pickup: z
    .object({
      enabled: z.boolean().default(true),
      address: z.string().max(300).default(''),
      instructions: z.string().max(300).default(''),
    })
    .default({ enabled: true, address: '', instructions: '' }),
  localDelivery: z
    .object({
      enabled: z.boolean().default(false),
      feeMode: z.enum(['fixed', 'zones', 'distance', 'free', 'to_agree']).default('fixed'),
      feeCents: z.number().int().min(0).default(0),
      zones: z.array(deliveryZoneSchema).max(100).default([]),
      distance: distanceFeeSchema.default(DEFAULT_DISTANCE_FEE),
      freeAboveCents: z.number().int().min(0).nullable().default(null),
      estimate: z.string().max(80).default(''),
    })
    .default({ enabled: false, feeMode: 'fixed', feeCents: 0, zones: [], distance: DEFAULT_DISTANCE_FEE, freeAboveCents: null, estimate: '' }),
  shipping: z
    .object({
      enabled: z.boolean().default(false),
      label: z.string().max(60).default('Envio pelos Correios / transportadora'),
      feeMode: z.enum(['fixed', 'to_agree', 'calculated']).default('to_agree'),
      feeCents: z.number().int().min(0).default(0),
      calculated: calculatedShippingSchema.default(DEFAULT_CALCULATED_SHIPPING),
      estimate: z.string().max(80).default(''),
    })
    .default({
      enabled: false,
      label: 'Envio pelos Correios / transportadora',
      feeMode: 'to_agree',
      feeCents: 0,
      calculated: DEFAULT_CALCULATED_SHIPPING,
      estimate: '',
    }),
  /** Descontos de frete por região (ver shippingRuleSchema). */
  rules: z.array(shippingRuleSchema).max(50).default([]),
});
export type DeliverySettings = z.infer<typeof deliverySettingsSchema>;

// ---------------------------------------------------------------- Pagamentos
export const PAYMENT_METHOD_TYPES = [
  'cash',
  'credit_card',
  'debit_card',
  'pix',
  'store_credit',
  'voucher',
  'bank_transfer',
  'boleto',
  'other',
] as const;
export type PaymentMethodTypeKey = (typeof PAYMENT_METHOD_TYPES)[number];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethodTypeKey, string> = {
  cash: 'Dinheiro',
  credit_card: 'Cartão de crédito',
  debit_card: 'Cartão de débito',
  pix: 'Pix',
  store_credit: 'Fiado',
  voucher: 'Vale',
  bank_transfer: 'Transferência',
  boleto: 'Boleto',
  other: 'Outro',
};

export const paymentMethodConfigSchema = z.object({
  id: z.string().min(1).max(40),
  type: z.enum(PAYMENT_METHOD_TYPES),
  label: z.string().min(1).max(40),
  enabled: z.boolean().default(true),
  feePercent: z.number().min(0).max(100).default(0),
  feeFixedCents: z.number().int().min(0).default(0),
  maxInstallments: z.number().int().min(1).max(24).default(1),
  daysToReceive: z.number().int().min(0).max(120).default(0),
  /** Conta financeira onde o dinheiro desta forma de pagamento cai (financeiro v2). */
  accountId: z.string().uuid().nullable().default(null),
});
export type PaymentMethodConfig = z.infer<typeof paymentMethodConfigSchema>;

export const PIX_KEY_TYPES = ['cpf', 'cnpj', 'email', 'phone', 'random'] as const;
export type PixKeyType = (typeof PIX_KEY_TYPES)[number];

export const pixSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  keyType: z.enum(PIX_KEY_TYPES).default('random'),
  key: z.string().max(77).default(''),
  merchantName: z.string().max(25).default(''),
  merchantCity: z.string().max(15).default(''),
});
export type PixSettings = z.infer<typeof pixSettingsSchema>;

export const DEFAULT_PAYMENT_METHODS: PaymentMethodConfig[] = [
  { id: 'cash', type: 'cash', label: 'Dinheiro', enabled: true, feePercent: 0, feeFixedCents: 0, maxInstallments: 1, daysToReceive: 0, accountId: null },
  { id: 'pix', type: 'pix', label: 'Pix', enabled: true, feePercent: 0, feeFixedCents: 0, maxInstallments: 1, daysToReceive: 0, accountId: null },
  { id: 'debit', type: 'debit_card', label: 'Cartão de débito', enabled: true, feePercent: 1.99, feeFixedCents: 0, maxInstallments: 1, daysToReceive: 1, accountId: null },
  { id: 'credit', type: 'credit_card', label: 'Cartão de crédito', enabled: true, feePercent: 4.99, feeFixedCents: 0, maxInstallments: 12, daysToReceive: 30, accountId: null },
  { id: 'fiado', type: 'store_credit', label: 'Fiado', enabled: true, feePercent: 0, feeFixedCents: 0, maxInstallments: 1, daysToReceive: 0, accountId: null },
];

export const PAYMENT_PROVIDERS = ['mercadopago', 'asaas'] as const;
export type PaymentProviderKey = (typeof PAYMENT_PROVIDERS)[number];
export const PAYMENT_PROVIDER_LABELS: Record<PaymentProviderKey, string> = { mercadopago: 'Mercado Pago', asaas: 'Asaas' };

/**
 * Pagamento online no catálogo, pela conta da própria loja no provedor (sem intermediação do Dezza).
 * Cada forma (Pix, cartão, boleto) pode usar um provedor conectado diferente.
 */
export const onlinePaymentSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  pixProvider: z.enum(PAYMENT_PROVIDERS).nullable().default(null),
  cardProvider: z.enum(PAYMENT_PROVIDERS).nullable().default(null),
  boletoProvider: z.enum(PAYMENT_PROVIDERS).nullable().default(null),
  /** Desconto para quem paga no Pix pelo site (0 = sem desconto). */
  pixDiscountPercent: z.number().min(0).max(30).default(0),
  /** Pedido pago online que não foi pago em X minutos é cancelado e o estoque volta. */
  expireUnpaidAfterMinutes: z.number().int().min(15).max(10080).default(60),
  /** Continua aceitando "pagar na entrega/retirada" junto com o pagamento online. */
  allowPayLater: z.boolean().default(true),
  /** Conta financeira onde cai o dinheiro recebido por cada provedor (financeiro v2). */
  providerAccounts: z
    .object({ mercadopago: z.string().uuid().nullable().default(null), asaas: z.string().uuid().nullable().default(null) })
    .default({ mercadopago: null, asaas: null }),
});
export type OnlinePaymentSettings = z.infer<typeof onlinePaymentSettingsSchema>;
const DEFAULT_ONLINE_PAYMENT: OnlinePaymentSettings = {
  enabled: false,
  pixProvider: null,
  cardProvider: null,
  boletoProvider: null,
  pixDiscountPercent: 0,
  expireUnpaidAfterMinutes: 60,
  allowPayLater: true,
  providerAccounts: { mercadopago: null, asaas: null },
};

export const paymentSettingsSchema = z.object({
  methods: z.array(paymentMethodConfigSchema).max(30).default(DEFAULT_PAYMENT_METHODS),
  pix: pixSettingsSchema.default({ enabled: false, keyType: 'random', key: '', merchantName: '', merchantCity: '' }),
  online: onlinePaymentSettingsSchema.default(DEFAULT_ONLINE_PAYMENT),
});
export type PaymentSettings = z.infer<typeof paymentSettingsSchema>;

// ---------------------------------------------------------------- Precificação
/**
 * Parâmetros de formação de preço (calculadora de preço do produto e prévia de preço na compra).
 * venda: preço = custo / (1 - encargos - margem). custo: preço = custo × (1 + margem) / (1 - encargos).
 * encargos = imposto + taxa de cartão + comissão + outros.
 */
export const PRICE_ROUNDING = ['none', 'ten_cents', 'x90', 'x99', 'whole', 'whole_up'] as const;
export const pricingSettingsSchema = z.object({
  taxPercent: z.number().min(0).max(60).default(0),
  cardFeePercent: z.number().min(0).max(20).default(0),
  commissionPercent: z.number().min(0).max(50).default(0),
  otherPercent: z.number().min(0).max(50).default(0),
  targetMarginPercent: z.number().min(0).max(90).default(40),
  marginMode: z.enum(['sale', 'cost']).default('sale'),
  rounding: z.enum(PRICE_ROUNDING).default('x90'),
});
export type PricingSettings = z.infer<typeof pricingSettingsSchema>;

// ---------------------------------------------------------------- Onboarding
export const onboardingSchema = z.object({
  dismissedCards: z.array(z.string()).default([]),
  completedTour: z.boolean().default(false),
});
export type Onboarding = z.infer<typeof onboardingSchema>;

// ---------------------------------------------------------------- Parsers
function safeParse<T extends z.ZodType>(schema: T, raw: unknown): z.infer<T> {
  const result = schema.safeParse(raw ?? {});
  if (result.success) return result.data;
  return schema.parse({});
}

export const parseGeneralSettings = (raw: unknown): GeneralSettings => safeParse(generalSettingsSchema, raw);
export const parseReceiptSettings = (raw: unknown): ReceiptSettings => safeParse(receiptSettingsSchema, raw);
export const parseSalesSettings = (raw: unknown): SalesSettings => safeParse(salesSettingsSchema, raw);
export const parseDeliverySettings = (raw: unknown): DeliverySettings => safeParse(deliverySettingsSchema, raw);
export const parsePaymentSettings = (raw: unknown): PaymentSettings => safeParse(paymentSettingsSchema, raw);
export const parsePricingSettings = (raw: unknown): PricingSettings => safeParse(pricingSettingsSchema, raw);
export const parseOnboarding = (raw: unknown): Onboarding => safeParse(onboardingSchema, raw);

export const SETTINGS_SECTIONS = {
  general: generalSettingsSchema,
  receipt: receiptSettingsSchema,
  sales: salesSettingsSchema,
  delivery: deliverySettingsSchema,
  payment: paymentSettingsSchema,
  pricing: pricingSettingsSchema,
} as const;
export type SettingsSection = keyof typeof SETTINGS_SECTIONS;

export const STORE_SEGMENTS = [
  { value: 'moda', label: 'Roupas e moda', categories: ['Blusas', 'Calças', 'Conjuntos', 'Vestidos', 'Acessórios'] },
  { value: 'moda_fitness', label: 'Moda fitness', categories: ['Tops', 'Leggings', 'Conjuntos', 'Macacões', 'Shorts'] },
  { value: 'calcados', label: 'Calçados', categories: ['Tênis', 'Sandálias', 'Botas', 'Chinelos'] },
  { value: 'beleza', label: 'Beleza e cosméticos', categories: ['Maquiagem', 'Cabelos', 'Skincare', 'Perfumes'] },
  { value: 'acessorios', label: 'Acessórios e bijuterias', categories: ['Brincos', 'Colares', 'Pulseiras', 'Anéis'] },
  { value: 'alimentos', label: 'Alimentos e bebidas', categories: ['Bebidas', 'Doces', 'Salgados', 'Mercearia'] },
  { value: 'eletronicos', label: 'Eletrônicos e celulares', categories: ['Acessórios', 'Capinhas', 'Carregadores', 'Fones'] },
  { value: 'casa', label: 'Casa e decoração', categories: ['Cozinha', 'Banho', 'Decoração', 'Organização'] },
  { value: 'pet', label: 'Pet shop', categories: ['Rações', 'Petiscos', 'Brinquedos', 'Higiene'] },
  { value: 'servicos', label: 'Serviços', categories: ['Serviços', 'Pacotes'] },
  { value: 'outro', label: 'Outro', categories: ['Geral'] },
] as const;

export const DEFAULT_FINANCE_CATEGORIES: { name: string; type: 'income' | 'expense'; color: string }[] = [
  { name: 'Vendas avulsas', type: 'income', color: '#0A875D' },
  { name: 'Serviços', type: 'income', color: '#1FC98E' },
  { name: 'Outras receitas', type: 'income', color: '#2563EB' },
  { name: 'Fornecedores', type: 'expense', color: '#CF2E36' },
  { name: 'Aluguel', type: 'expense', color: '#A86200' },
  { name: 'Salários', type: 'expense', color: '#7C3AED' },
  { name: 'Impostos', type: 'expense', color: '#475569' },
  { name: 'Marketing', type: 'expense', color: '#DB2777' },
  { name: 'Taxas e tarifas', type: 'expense', color: '#0891B2' },
  { name: 'Contas (água, luz, internet)', type: 'expense', color: '#EA580C' },
  { name: 'Outras despesas', type: 'expense', color: '#6B7485' },
];

export const PRODUCT_UNITS = [
  { value: 'un', label: 'Unidade (un)', fractional: false },
  { value: 'kg', label: 'Quilo (kg)', fractional: true },
  { value: 'g', label: 'Grama (g)', fractional: true },
  { value: 'l', label: 'Litro (l)', fractional: true },
  { value: 'ml', label: 'Mililitro (ml)', fractional: true },
  { value: 'm', label: 'Metro (m)', fractional: true },
  { value: 'cx', label: 'Caixa (cx)', fractional: false },
  { value: 'pct', label: 'Pacote (pct)', fractional: false },
  { value: 'par', label: 'Par', fractional: false },
] as const;
